import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useMenuIcon, useMenuName } from '@/hooks/useMenuIcon';
import { PageContainer, PageHeader } from '@/components/layout';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { API_BASE_URL as BASE } from '@/api/apiConfig';
import { StatsCard } from './StatsCard';
import { ServiceStatsChart } from './ServiceStatsChart';
import { CompletionRateChart } from './CompletionRateChart';
import { TrendChart } from './TrendChart';
import UserGuideModal from '@/components/guide/UserGuideModal';
import {
  dashboardApi,
  aiUsageApi,
  DashboardSummary,
  ServiceStats,
  CompletionRateData,
  TrendData,
  AiUsageSummary,
  AiUsageMonthly,
  AiUsageTopUser,
  AiUsageStatus,
  BillingPeriod,
} from '@/api/dashboardApi';
import { planApi } from '@/api/planApi';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { 
  TrendingUp, 
  Calendar, 
  Activity, 
  BarChart3,
  Loader2,
  AlertTriangle,
  Bot,
  Search,
  X,
  ArrowRight,
  CheckCircle2,
} from 'lucide-react';

interface SimpleUser {
  userId: string;
  email: string;
  name: string;
  businessNumber?: string;
  companyName?: string;
}

export const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const menuIcon = useMenuIcon();
  const menuName = useMenuName();
  const isSuperAdmin = user?.userRole === 'SUPER_ADMIN';
  const [searchParams, setSearchParams] = useSearchParams();
  const isClaimed = searchParams.get('claimed') === 'true';

  // 상태 관리
  const [error, setError] = useState<string | null>(null);
  const [trendPeriod, setTrendPeriod] = useState<'WEEK' | 'MONTH' | 'QUARTER'>('WEEK');
  const [showUserGuide, setShowUserGuide] = useState(false);
  const [planLimits, setPlanLimits] = useState<Record<string, number>>({});

  // 이용가이드 모달 표시 로직
  useEffect(() => {
    const dontShowAgain = localStorage.getItem('userGuideDontShowAgain');
    if (!dontShowAgain || dontShowAgain !== 'true') {
      // 첫 로그인 또는 "다음에 보기"를 체크하지 않은 경우
      setShowUserGuide(true);
    }
  }, []);

  // 플랜 제한 정보 가져오기
  useEffect(() => {
    planApi.getPlans()
      .then((res) => {
        const limits: Record<string, number> = {};
        res.data.forEach((plan) => {
          limits[plan.planId] = plan.monthlyLimit;
        });
        setPlanLimits(limits);
      })
      .catch(() => {
        // API 호출 실패 시 기본값 설정
        setPlanLimits({
          FREE: 60,
          STARTER: 1000,
          PROFESSIONAL: 5000,
          ENTERPRISE: 0,
        });
      });
  }, []);

  // 사용자 검색 (SUPER_ADMIN)
  const [searchInput, setSearchInput] = useState('');
  // null = 전체모드, [] = 검색결과없음, [...] = 찾아진사용자
  const [searchedUsers, setSearchedUsers] = useState<SimpleUser[] | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // 단일 userId 도출
  const getEffectiveUserId = (): string | undefined => {
    if (!isSuperAdmin) return user?.userId; // 일반 사용자는 본인 userId 사용
    if (!searchedUsers || searchedUsers.length !== 1) return undefined;
    return searchedUsers[0].userId;
  };

  const isBizMode = isSuperAdmin && !!searchedUsers && searchedUsers.length > 1;
  const bizUserIds = isBizMode ? searchedUsers!.map(u => u.userId) : [];

  // 데이터 상태 (useQuery로 관리)
  const targetUserId = getEffectiveUserId();
  
  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['dashboardSummary', targetUserId],
    queryFn: () => dashboardApi.getSummary(targetUserId),
    enabled: !isBizMode, // 사업자번호 모드일 때는 비활성화
  });

  const { data: serviceStats } = useQuery({
    queryKey: ['serviceStats', targetUserId],
    queryFn: () => dashboardApi.getServiceStats(targetUserId, 'ALL'),
    enabled: !isBizMode,
  });

  const { data: completionRate } = useQuery({
    queryKey: ['completionRate', targetUserId],
    queryFn: () => dashboardApi.getCompletionRate(targetUserId, 'ALL'),
    enabled: !isBizMode,
  });

  const { data: trendData } = useQuery({
    queryKey: ['trendData', trendPeriod, targetUserId],
    queryFn: () => dashboardApi.getTrend(trendPeriod, targetUserId),
    enabled: !isBizMode,
  });

  // AI 사용량 (단일 사용자 모드만 useQuery 적용)
  const { data: aiSummary } = useQuery({
    queryKey: ['aiSummary', targetUserId],
    queryFn: () => aiUsageApi.getSummary(targetUserId),
    enabled: !isBizMode && !!targetUserId,
  });

  const { data: aiMonthly } = useQuery({
    queryKey: ['aiMonthly', targetUserId],
    queryFn: () => aiUsageApi.getMonthly(targetUserId),
    enabled: !isBizMode && !!targetUserId,
  });

  const { data: aiTopUsers } = useQuery({
    queryKey: ['aiTopUsers'],
    queryFn: () => aiUsageApi.getTopUsers(),
    enabled: isSuperAdmin && searchedUsers === null,
  });

  // AI 사용량 상태 (팀 컨텍스트 포함)
  const { data: aiUsageStatus } = useQuery({
    queryKey: ['aiUsageStatus'],
    queryFn: () => dashboardApi.getAiUsageStatus(),
  });

  // 청구 기간 조회
  const { data: billingPeriod } = useQuery({
    queryKey: ['billingPeriod'],
    queryFn: () => dashboardApi.getBillingPeriod(),
  });

  // 사업자번호 모드용 상태 (기존 로직 유지)
  const [bizAiSummary, setBizAiSummary] = useState<AiUsageSummary | null>(null);
  const [bizAiMonthly, setBizAiMonthly] = useState<AiUsageMonthly | null>(null);
  const [bizLoading, setBizLoading] = useState(false);

  // 사용자 검색 실행
  const handleSearch = async () => {
    const kw = searchInput.trim();
    if (!kw) {
      setSearchedUsers(null);
      setSearchError(null);
      return;
    }
    try {
      setSearchLoading(true);
      setSearchError(null);
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const headers = { Authorization: `Bearer ${token}` };

      const toUser = (u: any): SimpleUser => ({
        userId: u.userId,
        email: u.email || '',
        name: u.name || u.userId,
        businessNumber: u.businessNumber || undefined,
        companyName: u.companyName || undefined,
      });

      // 아이디 / 이메일 / 사업자번호 3가지 기준 병렬 조회
      const [byId, byEmail, byBiz] = await Promise.allSettled([
        // ① 아이디 직접 조회
        axios.get(`${BASE}/api/users/${encodeURIComponent(kw)}`, { headers }),
        // ② 이메일 기준 검색
        axios.get(`${BASE}/api/users?page=0&size=100&email=${encodeURIComponent(kw)}`, { headers }),
        // ③ 사업자번호 기준 검색
        axios.get(`${BASE}/api/users?page=0&size=100&businessNumber=${encodeURIComponent(kw)}`, { headers }),
      ]);

      const found = new Map<string, SimpleUser>();

      // ① 아이디 결과
      if (byId.status === 'fulfilled') {
        const u = byId.value.data?.data ?? byId.value.data;
        if (u?.userId) found.set(u.userId, toUser(u));
      }

      // ② 이메일 결과
      if (byEmail.status === 'fulfilled') {
        const raw = byEmail.value.data?.data;
        const content: any[] = Array.isArray(raw) ? raw : (raw?.content ?? []);
        content.forEach(u => { if (u?.userId) found.set(u.userId, toUser(u)); });
      }

      // ③ 사업자번호 결과
      if (byBiz.status === 'fulfilled') {
        const raw = byBiz.value.data?.data;
        const content: any[] = Array.isArray(raw) ? raw : (raw?.content ?? []);
        content.forEach(u => { if (u?.userId) found.set(u.userId, toUser(u)); });
      }

      const result = Array.from(found.values());

      if (result.length === 0) {
        setSearchError(`'${kw}'에 대한 검색 결과가 없습니다.`);
        setSearchedUsers([]);
      } else {
        setSearchedUsers(result);
        setSearchError(null);
      }
    } catch {
      setSearchError('검색 중 오류가 발생했습니다.');
      setSearchedUsers([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchedUsers(null); // 전체 모드로 복귀
    setSearchError(null);
  };

  // 현재 상태 요약 텍스트
  const getSearchLabel = (): string => {
    if (searchedUsers === null) return '전체 사용자 합산';
    if (searchedUsers.length === 0) return '결과 없음';
    if (searchedUsers.length === 1) {
      const u = searchedUsers[0];
      return `${u.userId}${u.email ? ` (${u.email})` : ''}`;
    }
    const biz = searchedUsers[0].businessNumber;
    return `사업자번호 ${biz} · ${searchedUsers.length}개 계정 합산`;
  };

  // 사업자번호 모드용 데이터 로드
  const loadBizModeData = async () => {
    if (!isBizMode || bizUserIds.length === 0) return;
    
    try {
      setBizLoading(true);
      const biz = searchedUsers[0].businessNumber ?? '';
      const [summaryResults, monthlyResults] = await Promise.all([
        Promise.allSettled(bizUserIds.map(id => aiUsageApi.getSummary(id))),
        Promise.allSettled(bizUserIds.map(id => aiUsageApi.getMonthly(id))),
      ]);
      setBizAiSummary(aggregateAiSummaries(summaryResults, biz));
      setBizAiMonthly(aggregateAiMonthly(monthlyResults));
    } catch (err: any) {
      console.error('사업자번호 모드 데이터 로드 실패:', err);
    } finally {
      setBizLoading(false);
    }
  };

  // 사업자번호 모드 변경 시 데이터 로드
  useEffect(() => {
    if (isBizMode) {
      loadBizModeData();
    }
  }, [searchedUsers]);

  // 로딩 화면
  const isLoading = summaryLoading || bizLoading;
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <span className="ml-2 text-gray-600">데이터를 불러오는 중...</span>
      </div>
    );
  }

  // 에러 화면
  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <PageContainer maxWidth="full">
      {/* 진단 결과 연동 완료 배너 */}
      {isClaimed && (
        <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl text-green-800">
          <CheckCircle2 className="w-5 h-5 shrink-0 text-green-600" />
          <span className="flex-1 text-sm font-medium">진단 결과가 내 계정에 연동되었습니다. ALT 텍스트 자동 생성을 시작할 수 있습니다.</span>
          <button onClick={() => { const p = new URLSearchParams(searchParams); p.delete('claimed'); setSearchParams(p, { replace: true }); }} className="text-green-600 hover:text-green-800 p-1 rounded" aria-label="닫기">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* 결제 실패/정지로 AI 생성이 제한된 경우 안내 배너 */}
      {aiUsageStatus?.suspended && (
        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-800">
          <AlertTriangle className="w-5 h-5 shrink-0 text-red-600" />
          <span className="flex-1 text-sm font-medium">
            {aiUsageStatus.isTeamContext
              ? '결제 실패로 팀 구독이 정지되어 AI 생성이 제한되었습니다. 팀 관리자에게 결제 수단 확인을 요청해 주세요.'
              : '결제 실패로 서비스가 정지되어 AI 생성이 제한되었습니다. 요금제 페이지에서 결제 수단을 확인해 주세요.'}
          </span>
        </div>
      )}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            {menuIcon && <span className="text-4xl">{menuIcon}</span>}
            {menuName || '대시보드'}
          </h1>
          <p className="text-gray-600 mt-2">
            {isSuperAdmin 
              ? '전체 시스템 통계 및 분석' 
              : '내 ALT 생성 현황 및 통계'}
          </p>
        </div>

        {/* SUPER_ADMIN 전용: 검색 입력창 */}
        {isSuperAdmin && (
          <div className="flex flex-col gap-1.5 w-80">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  ref={inputRef}
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSearch()}
                  placeholder="아이디, 이메일, 사업자번호 검색"
                  className="pr-7 text-sm"
                />
                {searchInput && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <Button
                size="sm"
                onClick={handleSearch}
                disabled={searchLoading}
                className="shrink-0"
              >
                {searchLoading
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <Search className="w-4 h-4" />}
              </Button>
            </div>

            {/* 검색 결과 / 현재 조회 기준 표시 */}
            {searchError
              ? <p className="text-xs text-red-500">{searchError}</p>
              : <p className="text-xs text-gray-500">
                  {searchedUsers !== null && searchedUsers.length > 0 && (
                    <span className="font-medium text-primary">● {getSearchLabel()}</span>
                  )}
                  {searchedUsers === null && '● 전체 사용자 합산 중'}
                </p>
            }

            {/* 검색 적용 시: “전체 사용자 보기” 버튼 */}
            {searchedUsers !== null && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleClearSearch}
                className="w-full text-xs"
              >
                전체 사용자 보기
              </Button>
            )}
          </div>
        )}
      </div>

      {/* 검색결과 없음 안내 */}
      {isSuperAdmin && searchedUsers !== null && searchedUsers.length === 0 && (
        <Alert className="border-orange-300 bg-orange-50">
          <AlertDescription className="text-orange-700 text-sm">
            검색 결과가 없어 데이터가 표시되지 않습니다.
            검색어를 확인하거나
            <button onClick={handleClearSearch} className="ml-1 font-semibold underline text-orange-700 hover:text-orange-900">전체 사용자로 돌아가기</button>
            를 누르세요.
          </AlertDescription>
        </Alert>
      )}

      {/* 통계 카드 섹션 */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          <StatsCard
            title="총 처리량"
            value={summary.totalProcessed.toLocaleString()}
            icon={TrendingUp}
            description="누적 등록 이미지 수"
          />
          <StatsCard
            title="금일 처리량"
            value={summary.todayProcessed.toLocaleString()}
            icon={Calendar}
            description="오늘 처리된 이미지 수"
          />
          <StatsCard
            title="처리 완료율"
            value={`${summary.completionRate.toFixed(1)}%`}
            icon={Activity}
            description="AI·자동처리 포함 완료 비율"
          />
          <StatsCard
            title="AI 분석률"
            value={`${(summary.aiCompletionRate ?? 0).toFixed(1)}%`}
            icon={Bot}
            description="AI Vision 분석 완료 비율"
          />
          <StatsCard
            title="AI 호출"
            value={(summary.aiCompletedCount ?? summary.apiUsageCount).toLocaleString()}
            icon={BarChart3}
            description="현재 이미지 기준 AI 처리 건수 (삭제된 이미지 제외)"
          />
        </div>
      )}

      {/* 차트 섹션 - 2단 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 서비스별 처리량 */}
        {serviceStats && (
          <ServiceStatsChart services={serviceStats.services} />
        )}

        {/* 완료율 차트 */}
        {completionRate && (
          <CompletionRateChart data={completionRate} />
        )}
      </div>

      {/* 트렌드 차트 - 전체 너비 */}
      {trendData && (
        <TrendChart 
          data={trendData} 
          currentPeriod={trendPeriod}
          onPeriodChange={setTrendPeriod}
        />
      )}

      {/* ──────────────────────────── AI 사용량 섹션 ──────────────────────────── */}
      <AiUsageSection
        aiSummary={isBizMode ? bizAiSummary : aiSummary}
        aiMonthly={isBizMode ? bizAiMonthly : aiMonthly}
        aiTopUsers={aiTopUsers}
        isSuperAdmin={isSuperAdmin}
        aiUsageStatus={aiUsageStatus}
        billingPeriod={billingPeriod}
        planLimits={planLimits}
      />

      <UserGuideModal open={showUserGuide} onOpenChange={setShowUserGuide} />
    </PageContainer>
  );
};

// ============================================================
// AI 사용량 집계 헬퍼 (사업자번호 모드)
// ============================================================
function aggregateAiSummaries(
  results: PromiseSettledResult<AiUsageSummary>[],
  bizNum: string
): AiUsageSummary {
  const valid = results
    .filter(r => r.status === 'fulfilled')
    .map(r => (r as PromiseFulfilledResult<AiUsageSummary>).value);
  if (valid.length === 0) {
    return {
      targetUserId: `사업자번호: ${bizNum}`,
      currentYearMonth: '',
      currentMonthAiCount: 0,
      totalAiCount: 0,
      bySourceType: { WEBSITE_CRAWLING: 0, IMAGE_URL: 0, IMAGE_UPLOAD: 0 },
      subscriptionPlan: 'FREE',
    };
  }
  return {
    targetUserId: `사업자번호: ${bizNum}`,
    currentYearMonth: valid[0].currentYearMonth,
    currentMonthAiCount: valid.reduce((s, v) => s + v.currentMonthAiCount, 0),
    totalAiCount:        valid.reduce((s, v) => s + v.totalAiCount, 0),
    bySourceType: {
      WEBSITE_CRAWLING: valid.reduce((s, v) => s + v.bySourceType.WEBSITE_CRAWLING, 0),
      IMAGE_URL:        valid.reduce((s, v) => s + v.bySourceType.IMAGE_URL, 0),
      IMAGE_UPLOAD:     valid.reduce((s, v) => s + v.bySourceType.IMAGE_UPLOAD, 0),
    },
    subscriptionPlan: valid[0].subscriptionPlan,
  };
}

function aggregateAiMonthly(
  results: PromiseSettledResult<AiUsageMonthly>[]
): AiUsageMonthly {
  const valid = results
    .filter(r => r.status === 'fulfilled')
    .map(r => (r as PromiseFulfilledResult<AiUsageMonthly>).value);
  const monthMap = new Map<string, number>();
  valid.forEach(u => {
    u.monthly.forEach(m => {
      monthMap.set(m.yearMonth, (monthMap.get(m.yearMonth) ?? 0) + m.count);
    });
  });
  const monthly = Array.from(monthMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([yearMonth, count]) => ({ yearMonth, count }));
  return { targetUserId: '_aggregated_', monthly };
}

// ============================================================
// AI 사용량 섹션 컴포넌트
// ============================================================
interface AiUsageSectionProps {
  aiSummary: AiUsageSummary | null;
  aiMonthly: AiUsageMonthly | null;
  aiTopUsers: AiUsageTopUser[];
  isSuperAdmin: boolean;
  aiUsageStatus?: AiUsageStatus | null;
  billingPeriod?: { startDate: string; endDate: string; nextBillingDate: string } | null;
  planLimits: Record<string, number>;
}

function usageColor(count: number, limit: number): string {
  const rate = limit > 0 ? count / limit : 0;
  if (rate >= 1)   return 'text-red-600 font-bold';
  if (rate >= 0.9) return 'text-red-600 font-bold';
  if (rate >= 0.7) return 'text-orange-600 font-semibold';
  return 'text-gray-700';
}

function usageBarColor(count: number, limit: number): string {
  const rate = limit > 0 ? count / limit : 0;
  if (rate >= 0.9) return 'bg-red-500';
  if (rate >= 0.7) return 'bg-orange-400';
  return 'bg-blue-500';
}

const SOURCE_LABELS: Record<string, string> = {
  WEBSITE_CRAWLING: '웹페이지 크롤링',
  IMAGE_URL: '이미지 URL',
  IMAGE_UPLOAD: '이미지 업로드',
};

const AiUsageSection: React.FC<AiUsageSectionProps> = ({ aiSummary, aiMonthly, aiTopUsers, isSuperAdmin, aiUsageStatus, billingPeriod, planLimits }) => {
  const navigate = useNavigate();

  if (!aiSummary) return null;

  // 날짜 포맷팅 함수
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const now = new Date();
  const todayStr = now.toISOString().slice(0, 10);

  const handleDetailClick = () => {
    const startDate = billingPeriod?.startDate || `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    const endDate = billingPeriod?.endDate || todayStr;
    navigate(`/dashboard/ai-usage-daily?startDate=${startDate}&endDate=${endDate}`);
  };

  const plan = aiSummary.subscriptionPlan;
  const localLimit = planLimits[plan] ?? 0;
  const planLabel = plan;

  // 팀 컨텍스트인 경우 팀 한도와 사용량 사용
  const isTeamContext = aiUsageStatus?.isTeamContext ?? false;
  const displayLimit = isTeamContext ? (aiUsageStatus?.teamLimit ?? aiUsageStatus?.limit ?? localLimit) : (aiSummary.monthlyLimit ?? localLimit);
  const displayUsed = isTeamContext && aiUsageStatus?.role === 'OWNER' && aiUsageStatus?.teamTotalUsed !== null
    ? aiUsageStatus.teamTotalUsed
    : aiSummary.currentMonthAiCount;
  const individualUsed = aiUsageStatus?.used ?? aiSummary.currentMonthAiCount;

  const usageRate = displayLimit > 0 ? Math.min(displayUsed / displayLimit, 1) : 0;
  const isOverLimit = displayLimit > 0 && displayUsed > displayLimit;
  const isNearLimit = !isOverLimit && usageRate >= 0.9;

  const chartData = (aiMonthly?.monthly ?? []).slice(-6);

  const totalSource = Object.values(aiSummary.bySourceType).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-4">
      {/* 섹션 헤더 */}
      <div className="flex items-center gap-2 pt-2 border-t">
        <Bot className="w-5 h-5 text-purple-600" />
        <h2 className="text-xl font-bold text-gray-800">AI 사용량 현황</h2>
        <span className="text-xs text-gray-400 ml-2">ℹ️ 2026년 3월 5일 이후 AI 분석 완료 건수부터 집계됩니다.</span>
      </div>

      {/* 한도 초과 / 임박 경고 */}
      {(isOverLimit || isNearLimit) && (
        <Alert variant="destructive" className={isOverLimit ? '' : 'border-orange-400 bg-orange-50 text-orange-800'}>
          <AlertTriangle className="w-4 h-4" />
          <AlertDescription>
            {isOverLimit
              ? `이번 달 AI 분석 한도(${displayLimit.toLocaleString()}건)를 초과했습니다. 플랜 업그레이드를 검토하세요.`
              : `이번 달 사용량이 한도의 ${Math.round(usageRate * 100)}%에 달했습니다.`}
          </AlertDescription>
        </Alert>
      )}

      {/* 요약 카드 3개 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* 이번 달 사용량 */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">이번 달 AI 분석</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-1">
              <span className={`text-3xl font-bold ${usageColor(displayUsed, displayLimit)}`}>
                {displayUsed.toLocaleString()}
              </span>
              <span className="text-gray-400 text-sm mb-1">건</span>
              {isOverLimit && <Badge className="ml-2 mb-1 bg-red-600 text-white">한도 초과</Badge>}
            </div>
            {isTeamContext && aiUsageStatus && aiUsageStatus.teamTotalUsed !== null && aiUsageStatus.teamLimit !== null && (
              <p className="text-xs text-gray-500 mt-1">
                팀 사용량: {aiUsageStatus.teamTotalUsed.toLocaleString()} / {aiUsageStatus.teamLimit.toLocaleString()}건 (본인: {individualUsed.toLocaleString()}건)
              </p>
            )}
            <p className="text-xs text-gray-400 mt-1">{aiSummary.currentYearMonth} <span className="text-gray-300">(KST 기준)</span> · <span className="text-gray-400">삭제된 이미지 포함</span></p>
            {/* 사용량 진행 바 */}
            {displayLimit > 0 && (
              <div className="mt-3">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>사용 {Math.round(usageRate * 100)}%</span>
                  <span>한도 {displayLimit.toLocaleString()}건</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${usageBarColor(displayUsed, displayLimit)}`}
                    style={{ width: `${Math.min(usageRate * 100, 100)}%` }}
                  />
                </div>
              </div>
            )}
            {/* 상세 현황 링크 */}
            <div className="mt-3 pt-2 border-t border-gray-100">
              <button
                onClick={handleDetailClick}
                className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 transition-colors group w-full text-left"
              >
                <span>
                  상세 사용량 현황은{' '}
                  <span className="underline font-medium">AI 이용 현황 (일별 집계)</span>
                  에서 확인하세요.
                </span>
                <ArrowRight className="w-3 h-3 ml-1 group-hover:translate-x-0.5 transition-transform flex-shrink-0" />
              </button>
            </div>
          </CardContent>
        </Card>

        {/* 누적 사용량 */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">누적 AI 분석</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-1">
              <span className="text-3xl font-bold text-gray-800">{aiSummary.totalAiCount.toLocaleString()}</span>
              <span className="text-gray-400 text-sm mb-1">건</span>
            </div>
            <p className="text-xs text-gray-400 mt-1">전체 기간 실제 AI API 호출 이력</p>
            <p className="text-xs text-gray-400 mt-0.5">삭제된 이미지의 호출 포함</p>
            <div className="mt-3 pt-2 border-t border-gray-100">
              <p className="text-xs text-gray-500 leading-relaxed">
                💡 상단 <span className="font-medium text-gray-700">AI 호출</span> 수치는 현재 존재하는 이미지 기준이며, 이미지를 삭제하면 그만큼 줄어듭니다.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* 플랜 정보 */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">구독 플랜</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-700">{planLabel}</div>
            {isTeamContext && (
              <p className="text-xs text-gray-500 mt-1">
                팀: {aiUsageStatus?.teamName} ({aiUsageStatus?.role === 'OWNER' ? '팀장' : aiUsageStatus?.role === 'ADMIN' ? '관리자' : '팀원'})
              </p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              월 한도: {displayLimit > 0 ? `${displayLimit.toLocaleString()}건` : '무제한'}
            </p>
            {/* 청구 기간 */}
            {billingPeriod && (
              <div className="mt-2 pt-2 border-t border-gray-100">
                <p className="text-xs text-gray-500">이용 기간</p>
                <p className="text-xs text-gray-700 font-medium">{formatDate(billingPeriod.startDate)} ~ {formatDate(billingPeriod.endDate)}</p>
                <p className="text-xs text-gray-500">다음 결제일: {formatDate(billingPeriod.nextBillingDate)}</p>
              </div>
            )}
            {/* 엔터프라이즈 계약 정보 */}
            {aiUsageStatus?.enterpriseContract && (
              <div className="mt-2 pt-2 border-t border-gray-100">
                <p className="text-xs text-gray-500">계약명: {aiUsageStatus.enterpriseContract.contractName}</p>
                <p className="text-xs text-gray-500">월 이용료: ₩{aiUsageStatus.enterpriseContract.monthlyPrice.toLocaleString()}</p>
                <p className="text-xs text-gray-500">계약 기간: {new Date(aiUsageStatus.enterpriseContract.startDate).toLocaleDateString('ko-KR')} ~ {new Date(aiUsageStatus.enterpriseContract.endDate).toLocaleDateString('ko-KR')}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* 서비스 출처별 분류 + 월별 차트 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 서비스 출처별 */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">서비스별 AI 분석 현황 (이번 달)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {Object.entries(aiSummary.bySourceType).map(([key, cnt]) => {
              const pct = totalSource > 0 ? Math.round(cnt / totalSource * 100) : 0;
              return (
                <div key={key}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">{SOURCE_LABELS[key] ?? key}</span>
                    <span className="font-medium">{cnt.toLocaleString()}건 ({pct}%)</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div className="h-2 rounded-full bg-purple-500" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* 월별 추이 차트 */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">월별 AI 분석 추이</CardTitle>
          </CardHeader>
          <CardContent>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="yearMonth" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v: any) => [`${v}건`, 'AI 분석']} />
                  <Bar dataKey="count" name="AI 분석" fill="#8b5cf6" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[200px] text-gray-400 text-sm">
                아직 월별 데이터가 없습니다.
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* SUPER_ADMIN 전용: Top 사용자 테이블 */}
      {isSuperAdmin && aiTopUsers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">이번 달 사용자별 AI 분석 현황</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-gray-500">
                    <th className="py-2 px-3 text-left">#</th>
                    <th className="py-2 px-3 text-left">사용자 ID</th>
                    <th className="py-2 px-3 text-right">AI 분석 건수</th>
                  </tr>
                </thead>
                <tbody>
                  {aiTopUsers.map((u, idx) => (
                    <tr key={u.userId} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="py-2 px-3 text-gray-400">{idx + 1}</td>
                      <td className="py-2 px-3 font-medium">{u.userId}</td>
                      <td className="py-2 px-3 text-right font-semibold">{u.count.toLocaleString()}건</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

    </div>
  );
};
